export class ApiError extends Error {
  constructor(message: string, public status: number) { super(message); }
}

export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    headers: body !== undefined ? { "content-type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: AbortSignal.timeout(20_000),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try { msg = ((await res.json()) as { error: string }).error; } catch {}
    throw new ApiError(msg, res.status);
  }
  return res.json() as Promise<T>;
}

export interface PublicUser { id: string; username: string | null; firstName: string; lastName: string; avatar: string; bio: string; isPrivate: boolean }

export interface SiteTab { id: string; title: string; emoji?: string; symbol?: string }
export interface SiteMetaJson { name: string; icon?: string; symbol?: string; description?: string; bullets?: string[]; tabs?: SiteTab[]; colorScheme?: "light" | "dark" | "auto"; background?: string; backgroundDark?: string; profile?: SiteProfileLink }
/** Where the site shows a person's profile: hash route with "{id}", in meta.json tab `tab` (default: the first). */
export interface SiteProfileLink { tab?: string; route: string }
export interface TodayInfo { day: string; siteId: string | null; title: string | null; meta: SiteMetaJson | null; updatedAt: number | null; today: string }

export interface Me extends PublicUser {
  email: string;
  admin: boolean;
  following: string[];
  followers: string[];
  /** Incoming pending follow requests (user ids). */
  requests: string[];
  /** My outgoing requests awaiting approval. */
  requested: string[];
  /** People I've blocked. */
  blocked: string[];
  /** Unread inbox count. */
  unread: number;
  today: string;
  /** Origin of the sandbox domain; empty means same origin (local dev). */
  sandboxOrigin: string;
}

export interface SiteMeta { id: string; name: string; title?: string; meta?: SiteMetaJson | null; createdAt: number; updatedAt: number; errors: string[]; prompt?: string; status: "draft" | "generating" | "ready" | "error"; log?: string[]; inRotation: boolean }
export interface Site extends SiteMeta { files: Record<string, string>; compiled: Record<string, string> }
export interface Job { siteId: string; step: number; model: string; updatedAt: number }
export interface AdminIndex { sites: SiteMeta[]; days: { day: string; siteId: string }[]; today: string; autoGenerate: boolean; jobs: Job[] }

/** Normalize the me view: resolve an empty sandboxOrigin to this page's origin. */
export function normalizeMe(me: Me): Me {
  return { ...me, sandboxOrigin: me.sandboxOrigin || location.origin };
}

export interface LiveState {
  step: number; startedAt: number; text: string;
  tool?: { name: string; path?: string; code?: string; chars: number };
  usage?: { prompt: number; completion: number };
}
export interface LiveResponse { status: SiteMeta["status"]; log: string[]; errors: string[]; updatedAt: number; live: LiveState | null; jobs: Job[] }

export interface InboxItem {
  id: string; kind: "follow" | "request" | "approved" | "app"; title: string; body: string;
  url: string | null; day: string | null; fromUser: string | null; createdAt: number; readAt: number | null;
}
export interface ChatItem { role: "user" | "assistant" | "tool"; text: string }
